import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { Location } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { OKTA_SSO_ROUTE_PREFIX } from 'common/auth/constants';
import { DEVICE_HANDOFF_PATH } from 'common/deviceHandoff/state/constants';
import { selectIsDeviceHandoffModalOpen } from 'common/deviceHandoff/state/selectors';
import { useNavigationBlockerContext } from 'common/routing/blocking/NavigationBlockerContext';

interface DeviceHandoffLeaveGuard {
    isLeaveConfirmationVisible: boolean;
    confirmLeave: () => void;
    cancelLeave: () => void;
}

export function useBlockNavigationDuringDeviceHandoff(onLeave: () => void): DeviceHandoffLeaveGuard {
    const isDeviceHandoffModalOpen = useSelector(selectIsDeviceHandoffModalOpen);
    const [isLeaveConfirmationVisible, setLeaveConfirmationVisible] = useState(false);

    const blockerIdRef = useRef(`device-handoff-${uuid()}`);
    const isDeviceHandoffModalOpenRef = useRef(isDeviceHandoffModalOpen);
    isDeviceHandoffModalOpenRef.current = isDeviceHandoffModalOpen;

    const blockerContext = useNavigationBlockerContext();

    useEffect(() => {
        if (!blockerContext) {
            return;
        }

        const shouldBlockNavigation = ({ nextLocation }: { nextLocation: Location }): boolean => {
            if (!isDeviceHandoffModalOpenRef.current) {
                return false;
            }
            if (nextLocation.pathname.startsWith(OKTA_SSO_ROUTE_PREFIX)) {
                return false;
            }

            return !nextLocation.pathname.startsWith(DEVICE_HANDOFF_PATH);
        };

        blockerContext.register(blockerIdRef.current, shouldBlockNavigation);

        return () => blockerContext.unregister(blockerIdRef.current);
    }, [blockerContext]);

    const blocker = blockerContext?.blocker;
    const isThisBlockerActive = blockerContext?.activeBlockerId === blockerIdRef.current;

    useEffect(() => {
        if (blocker?.state === 'blocked' && isThisBlockerActive) {
            setLeaveConfirmationVisible(true);
        }
    }, [blocker?.state, isThisBlockerActive]);

    const cancelLeave = (): void => {
        setLeaveConfirmationVisible(false);
        blocker?.reset?.();
    };

    const confirmLeave = (): void => {
        setLeaveConfirmationVisible(false);
        onLeave();
        blocker?.proceed?.();
    };

    return { isLeaveConfirmationVisible, confirmLeave, cancelLeave };
}
